/**
 * Firestore Service Layer
 * 
 * This module provides all database operations for the items collection.
 * All functions handle errors and provide consistent interfaces for the UI layer.
 */

import { collection, getDocs, addDoc, doc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase/config';

// Collection name in Firestore
const COLLECTION_NAME = 'catecheses';

/**
 * Retrieves all items from Firestore
 * Items are sorted by datum (date) in ascending order
 * 
 * @returns {Promise<Array>} Array of item objects with id and data fields
 * @throws {Error} If the Firestore query fails
 */
export const getItems = async () => {
  try {
    const itemsCollection = collection(db, COLLECTION_NAME);
    // Simple query without compound sorting to work with emulator
    // In production, you can enable the compound index defined in firestore.indexes.json
    const querySnapshot = await getDocs(itemsCollection);
    
    console.log('📊 Firestore query returned', querySnapshot.size, 'documents');
    
    // Transform Firestore documents into plain objects
    const items = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      console.log('📄 Document:', doc.id, data);
      items.push({
        id: doc.id,
        ...data
      });
    });
    
    console.log('✅ Total items fetched:', items.length);
    
    // Sort by date (datum) in ascending order
    items.sort((a, b) => {
      if (a.datum < b.datum) return -1;
      if (a.datum > b.datum) return 1;
      return 0;
    });
    
    return items;
  } catch (error) {
    console.error('Error getting items:', error);
    throw error;
  }
};

/**
 * Adds a new item to Firestore
 * Automatically adds timestamps and user tracking (createdBy, updatedBy)
 * 
 * @param {Object} item - The item data to add
 * @param {string} item.etappe - Required: The etappe/stage
 * @param {string} item.datum - Required: The date
 * @param {string} [item.bisdom] - Optional: Diocese
 * @param {string} [item.parochie] - Optional: Parish
 * @param {string} [item.gemeenschap] - Optional: Community
 * @param {string} [item.catechistVoornaam] - Optional: Catechist first name
 * @param {string} [item.catechistAchternaam] - Optional: Catechist last name
 * @param {string} [item.catechistEmail] - Optional: Catechist email
 * @returns {Promise<Object>} The created item with id, timestamps, and user info
 * @throws {Error} If the Firestore add operation fails
 */
export const addItem = async (item) => {
  try {
    // Get current user info
    const user = auth.currentUser;
    const userEmail = user ? user.email : 'unknown';
    
    // Add server timestamps and user tracking
    const itemWithTimestamp = {
      ...item,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: userEmail,
      updatedBy: userEmail
    };
    
    const docRef = await addDoc(collection(db, COLLECTION_NAME), itemWithTimestamp);
    
    // Return the item with a client-side timestamp for immediate UI update
    return {
      id: docRef.id,
      ...item,
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: userEmail,
      updatedBy: userEmail
    };
  } catch (error) {
    console.error('Error adding item:', error);
    throw error;
  }
};

/**
 * Updates an existing item in Firestore
 * Automatically updates the updatedAt timestamp and updatedBy user
 * 
 * @param {string} id - The document ID to update
 * @param {Object} updates - Object containing the fields to update
 * @returns {Promise<void>}
 * @throws {Error} If the Firestore update operation fails
 */
export const updateItem = async (id, updates) => {
  try {
    // Get current user info
    const user = auth.currentUser;
    const userEmail = user ? user.email : 'unknown';
    
    const itemRef = doc(db, COLLECTION_NAME, id);
    // Update the document with user tracking and timestamp
    await updateDoc(itemRef, {
      ...updates,
      updatedAt: serverTimestamp(),
      updatedBy: userEmail
    });
  } catch (error) {
    console.error('Error updating item:', error);
    throw error;
  }
};

/**
 * Deletes an item from Firestore
 * 
 * @param {string} id - The document ID to delete
 * @returns {Promise<void>}
 * @throws {Error} If the Firestore delete operation fails
 */
export const deleteItem = async (id) => {
  try {
    await deleteDoc(doc(db, COLLECTION_NAME, id));
  } catch (error) {
    console.error('Error deleting item:', error);
    throw error;
  }
};
